/**
 * useArtifact — hook for document artifact state management.
 *
 * Provides content, dirty tracking, undo/redo for a single keyed artifact.
 * The artifact is stored in Redux at state.artifacts[artifactId].
 *
 * Usage:
 *   const { content, dirty, canUndo, canRedo, commit, undo, redo, load, reset, setDirty }
 *     = useArtifact('subtitle:/fi/ohjelmat/.../finnish');
 */

import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  loadArtifact,
  commitArtifact,
  undoArtifact,
  redoArtifact,
  setArtifactDirty,
  resetArtifact,
} from '../actions/artifact';

const useArtifact = (artifactId) => {
  const dispatch = useDispatch();

  const artifact = useSelector((state) => state.artifacts?.[artifactId]);

  const content = artifact?.content ?? '';
  const dirty = artifact?.dirty ?? false;
  const canUndo = (artifact?.undoStack?.length ?? 0) > 0;
  const canRedo = (artifact?.redoStack?.length ?? 0) > 0;
  const undoStack = artifact?.undoStack ?? [];
  const redoStack = artifact?.redoStack ?? [];

  const load = useCallback(
    (newContent) => {
      dispatch(loadArtifact(artifactId, newContent));
    },
    [dispatch, artifactId],
  );

  const commit = useCallback(
    (newContent, label = '') => {
      dispatch(commitArtifact(artifactId, newContent, label));
    },
    [dispatch, artifactId],
  );

  const undo = useCallback(() => {
    dispatch(undoArtifact(artifactId));
  }, [dispatch, artifactId]);

  const redo = useCallback(() => {
    dispatch(redoArtifact(artifactId));
  }, [dispatch, artifactId]);

  const setDirty = useCallback(
    (dirtyFlag) => {
      dispatch(setArtifactDirty(artifactId, dirtyFlag));
    },
    [dispatch, artifactId],
  );

  const reset = useCallback(() => {
    dispatch(resetArtifact(artifactId));
  }, [dispatch, artifactId]);

  return {
    content,
    dirty,
    canUndo,
    canRedo,
    undoStack,
    redoStack,
    load,
    commit,
    undo,
    redo,
    setDirty,
    reset,
  };
};

export default useArtifact;
